import { FormControl, FormHelperText, Slider as MuiSlider, Typography } from '@mui/material';
import React from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';

import { SliderMark } from 'types/shared/slider-mark.type';
import slugify from 'utils/slugify';

type SliderProps = {
  sx?: any;
  label: string;
  disabled?: boolean;
  hiddenLabel?: boolean;
  margin?: 'dense' | 'none' | 'normal';
  step?: number | null;
  min?: number;
  max?: number;
  marks?: boolean | SliderMark[];
  error?: boolean;
  helperText?: React.ReactNode;
  getAriaValueText?: ((value: number, index: number) => string) | undefined;
  onChangeEvent?: (e?: React.BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>;
  onChangeValue?: ((event: Event | React.SyntheticEvent<Element, Event>, value: number | number[]) => void) | undefined;
};

type SliderExtProps =
  | {
      value?: number | number[] | undefined;
      controller: UseControllerProps<any>;
    }
  | {
      value: number | number[] | undefined;
      controller?: UseControllerProps<any>;
    };

const Slider: React.FC<SliderProps & SliderExtProps> = ({
  label,
  value,
  controller,
  step,
  min,
  max,
  sx,
  marks,
  error,
  helperText,
  disabled = false,
  hiddenLabel = false,
  margin = 'none',
  getAriaValueText,
  onChangeEvent,
  onChangeValue,
}) => {
  return (
    <FormControl fullWidth margin={margin} sx={{ paddingRight: 4, paddingLeft: 4, paddingTop: 2 }}>
      {!hiddenLabel && (
        <Typography sx={{ paddingBottom: 3 }} variant="caption" component="label" htmlFor={`${slugify(label)}-label`}>
          {label}
        </Typography>
      )}
      {controller ? (
        <Controller
          {...controller}
          render={({ field, fieldState }) => {
            return (
              <>
                <MuiSlider
                  {...field}
                  value={field.value}
                  step={step}
                  min={min}
                  sx={sx}
                  max={max}
                  marks={marks}
                  valueLabelDisplay="on"
                  valueLabelFormat={(value) => (value === -1 ? 'N/A' : value)}
                  getAriaValueText={getAriaValueText}
                  disabled={disabled}
                  onChangeCommitted={(_, data) => {
                    field.onChange(data);
                    if (onChangeEvent) onChangeEvent();
                  }}
                  onChange={(_, data) => {
                    field.onChange(data);
                  }}
                />
                {fieldState.invalid && <FormHelperText error={fieldState.invalid}>{fieldState.error?.message}</FormHelperText>}
              </>
            );
          }}
        />
      ) : (
        <>
          <MuiSlider
            value={value}
            valueLabelDisplay="on"
            step={step}
            min={min}
            max={max}
            marks={marks}
            getAriaValueText={getAriaValueText}
            disabled={disabled}
            onChangeCommitted={(_, data) => {
              if (onChangeValue) onChangeValue(_, data);
            }}
          />
          {error && <FormHelperText error={error}>{helperText}</FormHelperText>}
        </>
      )}
    </FormControl>
  );
};

export default React.memo(Slider);
